'use client';

/** Bagian 6 & 66 — Form register dengan validasi yang jelas. */
import { useState } from 'react';
import { useRouter } from 'next/navigation';

export function RegisterForm() {
  const router = useRouter();
  const [form, setForm] = useState({
    fullName: '', email: '', organizationName: '', phone: '',
    password: '', confirm: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  // Indikator kekuatan password sederhana dan jujur.
  const strength = (() => {
    const p = form.password;
    if (!p) return { level: 0, label: '', color: '' };
    let score = 0;
    if (p.length >= 8) score++;
    if (p.length >= 12) score++;
    if (/[a-z]/.test(p) && /[A-Z]/.test(p)) score++;
    if (/\d/.test(p)) score++;
    if (/[^A-Za-z0-9]/.test(p)) score++;
    if (score <= 2) return { level: 1, label: 'Lemah', color: 'var(--rd-danger)' };
    if (score <= 3) return { level: 2, label: 'Cukup', color: 'var(--rd-warning)' };
    return { level: 3, label: 'Kuat', color: 'var(--rd-success)' };
  })();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (form.password !== form.confirm) {
      setError('Konfirmasi password tidak sama.');
      return;
    }
    if (form.password.length < 8) {
      setError('Password minimal 8 karakter.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: form.fullName,
          email: form.email,
          password: form.password,
          organizationName: form.organizationName,
          phone: form.phone || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error ?? 'Pendaftaran gagal. Silakan coba lagi.');
        return;
      }
      router.replace(data.redirectTo ?? '/marketplace/stores');
      router.refresh();
    } catch {
      setError('Tidak dapat menghubungi server. Periksa koneksi Anda.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={submit}>
      {error && (
        <div className="rd-alert rd-alert-error" style={{ marginBottom: 14 }}>
          <div>{error}</div>
        </div>
      )}

      <div className="rd-field">
        <label className="rd-label">Nama Lengkap</label>
        <input className="rd-input" value={form.fullName} onChange={set('fullName')}
          placeholder="Rian Wijaya" required autoFocus />
      </div>

      <div className="rd-field">
        <label className="rd-label">Nama Bisnis / Toko</label>
        <input className="rd-input" value={form.organizationName} onChange={set('organizationName')}
          placeholder="Toko Sinar Jaya" required />
        <div className="rd-hint">Akan menjadi nama organisasi Anda di Sellora.</div>
      </div>

      <div className="rd-field">
        <label className="rd-label">Email</label>
        <input type="email" className="rd-input" value={form.email} onChange={set('email')}
          placeholder="nama@perusahaan.com" required autoComplete="email" />
      </div>

      <div className="rd-field">
        <label className="rd-label">Nomor WhatsApp <span style={{ fontWeight: 400, color: 'var(--rd-secondary)' }}>(opsional)</span></label>
        <input className="rd-input" value={form.phone} onChange={set('phone')} placeholder="08123456789" />
      </div>

      <div className="rd-field">
        <label className="rd-label">Password</label>
        <input type="password" className="rd-input" value={form.password} onChange={set('password')}
          placeholder="Minimal 8 karakter" required autoComplete="new-password" />
        {form.password && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 5 }}>
            <div style={{ flex: 1, height: 4, borderRadius: 999, background: 'var(--rd-bg)', overflow: 'hidden' }}>
              <div style={{ width: `${(strength.level / 3) * 100}%`, height: '100%', background: strength.color }} />
            </div>
            <span style={{ fontSize: 11, color: strength.color, fontWeight: 600, minWidth: 44 }}>
              {strength.label}
            </span>
          </div>
        )}
      </div>

      <div className="rd-field">
        <label className="rd-label">Konfirmasi Password</label>
        <input type="password" className="rd-input" value={form.confirm} onChange={set('confirm')}
          placeholder="Ulangi password" required autoComplete="new-password" />
      </div>

      <button type="submit" className="rd-btn rd-btn-primary" disabled={loading}
        style={{ width: '100%', justifyContent: 'center', padding: '9px 14px', fontSize: 13.5, marginTop: 4 }}>
        {loading ? 'Membuat akun…' : 'Buat Akun'}
      </button>

      <p style={{ fontSize: 11, color: 'var(--rd-secondary)', marginTop: 12, marginBottom: 0, lineHeight: 1.55 }}>
        Dengan mendaftar, Anda menyetujui Ketentuan Layanan dan Kebijakan Privasi Sellora.
      </p>
    </form>
  );
}
